/**
 * CompetitorImportWizard Component
 * Import from another app (Mint, YNAB, Quicken), routed at /import/apps.
 *
 * Steps:
 * 1. Select source app
 * 2. Upload the export (upload/init, pre-signed POST, upload/complete)
 * 3. Wait for processing (the document is polled until its staged import is ready)
 * 4. Map the app's categories (/v1/category-mappings)
 * Then the routed import wizard (/import?id=...) confirms the account, shows the
 * transactions for review and commits them. Nothing is imported before that.
 *
 * WP-IMPORT-1: this wizard used to upload with a multipart POST to /v1/documents
 * and commit without a confirmed account; core has neither (the commit answers
 * 400 without one). It now uses only the endpoints the routed wizard uses.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { CategoryMapper } from '@/components/documents/CategoryMapping';
import { useUploadDocument, useStagedImportByDocumentId } from '@/hooks/useDocuments';
import { ApiClientError } from '@/services/api';
import type { CompetitorApp, CompetitorImportStep } from '@/types/document';
import { getFileExtension } from '@/utils/uploadMimeType';
import { cn } from '@/utils/cn';

// ============================================================================
// Types
// ============================================================================

interface CompetitorAppInfo {
  id: CompetitorApp;
  name: string;
  description: string;
  fileTypes: string[];
  icon: React.ReactNode;
}

interface UploadErrorState {
  message: string;
  showUpgrade: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const COMPETITOR_APPS: CompetitorAppInfo[] = [
  {
    id: 'mint',
    name: 'Mint',
    description: 'Import transactions from a Mint CSV export (transactions.csv)',
    fileTypes: ['.csv'],
    icon: <MintIcon className="w-8 h-8" />,
  },
  {
    id: 'ynab',
    name: 'YNAB',
    description: 'Import from a YNAB register CSV or YNAB plan JSON',
    fileTypes: ['.csv', '.json'],
    icon: <YnabIcon className="w-8 h-8" />,
  },
  {
    id: 'quicken',
    name: 'Quicken',
    description: 'Import from a Quicken QIF or QFX export',
    fileTypes: ['.qif', '.qfx'],
    icon: <QuickenIcon className="w-8 h-8" />,
  },
];

const STEPS: { key: CompetitorImportStep; label: string; number: number }[] = [
  { key: 'select_app', label: 'Select App', number: 1 },
  { key: 'upload', label: 'Upload', number: 2 },
  { key: 'processing', label: 'Process', number: 3 },
  { key: 'category_mapping', label: 'Map Categories', number: 4 },
];

const PREMIUM_REQUIRED_MARKER = 'requires premium subscription';

// ============================================================================
// Helper Functions
// ============================================================================

function getStepNumber(step: CompetitorImportStep): number {
  return STEPS.find((s) => s.key === step)?.number ?? 1;
}

function getAcceptedFileTypes(app: CompetitorApp): string[] {
  return COMPETITOR_APPS.find((a) => a.id === app)?.fileTypes ?? ['.csv'];
}

function isCompetitorApp(value: string | null): value is CompetitorApp {
  return COMPETITOR_APPS.some((app) => app.id === value);
}

/** A displayable message for a failed upload (no file content is ever shown). */
function getUploadError(error: unknown): UploadErrorState {
  if (error instanceof ApiClientError) {
    if (error.status === 402) {
      return { message: 'You have reached your upload limit.', showUpgrade: true };
    }
    const detail = error.detail || error.message;
    return { message: detail, showUpgrade: detail.includes(PREMIUM_REQUIRED_MARKER) };
  }
  return {
    message: error instanceof Error ? error.message : 'Upload failed. Please try again.',
    showUpgrade: false,
  };
}

// ============================================================================
// Main Component
// ============================================================================

export default function CompetitorImportWizard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const appParam = searchParams.get('app');
  const [selectedApp, setSelectedApp] = useState<CompetitorApp | null>(
    isCompetitorApp(appParam) ? appParam : null
  );
  const [documentId, setDocumentId] = useState<string | null>(searchParams.get('documentId'));
  const [currentStep, setCurrentStep] = useState<CompetitorImportStep>(() => {
    if (searchParams.get('documentId') && isCompetitorApp(appParam)) return 'processing';
    if (isCompetitorApp(appParam)) return 'upload';
    return 'select_app';
  });
  const [uploadError, setUploadError] = useState<UploadErrorState | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadDocument();
  // Polled only while processing; the staged import id is kept once handed over.
  const processing = useStagedImportByDocumentId(currentStep === 'processing' ? documentId : null);
  const [stagedImportId, setStagedImportId] = useState<string | null>(null);

  const goToStep = useCallback(
    (step: CompetitorImportStep, params: { app?: CompetitorApp | null; documentId?: string | null }) => {
      setCurrentStep(step);
      const next = new URLSearchParams();
      const app = params.app === undefined ? selectedApp : params.app;
      const doc = params.documentId === undefined ? documentId : params.documentId;
      if (app) next.set('app', app);
      if (doc) next.set('documentId', doc);
      setSearchParams(next, { replace: true });
    },
    [documentId, selectedApp, setSearchParams]
  );

  // The staged import is ready for review: map categories next.
  useEffect(() => {
    if (currentStep === 'processing' && processing.stagedImportId) {
      setStagedImportId(processing.stagedImportId);
      setCurrentStep('category_mapping');
    }
  }, [currentStep, processing.stagedImportId]);

  const handleSelectApp = useCallback(
    (app: CompetitorApp) => {
      setSelectedApp(app);
      setUploadError(null);
      goToStep('upload', { app, documentId: null });
    },
    [goToStep]
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (!file || !selectedApp) return;

      setUploadError(null);
      const accepted = getAcceptedFileTypes(selectedApp);
      if (!accepted.includes(`.${getFileExtension(file.name)}`)) {
        setUploadError({ message: `Please choose a ${accepted.join(' or ')} file.`, showUpgrade: false });
        return;
      }

      try {
        const result = await uploadMutation.mutateAsync(file);
        setDocumentId(result.documentId);
        goToStep('processing', { documentId: result.documentId });
      } catch (error) {
        setUploadError(getUploadError(error));
      }
    },
    [goToStep, selectedApp, uploadMutation]
  );

  const handleMappingComplete = useCallback(() => {
    if (!stagedImportId) return;
    // Account confirmation, review and commit happen in the routed import wizard.
    navigate(`/import?id=${encodeURIComponent(stagedImportId)}&step=account_resolution`);
  }, [navigate, stagedImportId]);

  const handleStartOver = useCallback(() => {
    setDocumentId(null);
    setStagedImportId(null);
    setUploadError(null);
    goToStep('upload', { documentId: null });
  }, [goToStep]);

  const handleBack = useCallback(() => {
    setSelectedApp(null);
    setStagedImportId(null);
    setUploadError(null);
    goToStep('select_app', { app: null, documentId: null });
  }, [goToStep]);

  const handleCancel = useCallback(() => {
    navigate('/documents');
  }, [navigate]);

  const handleUpgrade = useCallback(() => {
    navigate('/billing');
  }, [navigate]);

  return (
    <div className="max-w-4xl mx-auto">
      <WizardHeader currentStep={currentStep} />

      {currentStep === 'select_app' && (
        <SelectAppStep onSelectApp={handleSelectApp} onCancel={handleCancel} />
      )}

      {currentStep === 'upload' && selectedApp && (
        <UploadStep
          app={selectedApp}
          fileInputRef={fileInputRef}
          isUploading={uploadMutation.isPending}
          error={uploadError}
          onFileSelect={handleFileSelect}
          onUpgrade={handleUpgrade}
          onBack={handleBack}
          onCancel={handleCancel}
        />
      )}

      {currentStep === 'processing' && (
        <ProcessingStep
          status={processing.documentStatus}
          failed={processing.documentStatus === 'failed' || processing.documentStatus === 'error'}
          failureMessage={processing.documentError}
          isOrphaned={processing.isOrphanedDocument}
          onStartOver={handleStartOver}
          onCancel={handleCancel}
        />
      )}

      {currentStep === 'category_mapping' && stagedImportId && selectedApp && (
        <CategoryMapper
          stagedImportId={stagedImportId}
          sourceApp={selectedApp}
          onComplete={handleMappingComplete}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}

// ============================================================================
// Wizard Header Component
// ============================================================================

interface WizardHeaderProps {
  currentStep: CompetitorImportStep;
}

function WizardHeader({ currentStep }: WizardHeaderProps) {
  const currentStepNumber = getStepNumber(currentStep);

  return (
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-gray-900">Import from Another App</h1>
      <p className="mt-1 text-gray-600">
        Step {currentStepNumber} of {STEPS.length}, then review and confirm the account
      </p>

      <div className="mt-4">
        <div className="flex items-center">
          {STEPS.map((step, index) => (
            <div key={step.key} className="flex items-center">
              <div
                className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium',
                  step.number < currentStepNumber
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : step.number === currentStepNumber
                      ? 'border-primary-600 text-primary-600'
                      : 'border-gray-300 text-gray-400'
                )}
              >
                {step.number < currentStepNumber ? <CheckIcon className="w-5 h-5" /> : step.number}
              </div>
              <span
                className={cn(
                  'ml-2 text-sm hidden sm:inline',
                  step.number <= currentStepNumber ? 'text-gray-900 font-medium' : 'text-gray-400'
                )}
              >
                {step.label}
              </span>
              {index < STEPS.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2 sm:mx-4 min-w-[20px] sm:min-w-[40px]',
                    step.number < currentStepNumber ? 'bg-primary-600' : 'bg-gray-200'
                  )}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 1: Select App
// ============================================================================

interface SelectAppStepProps {
  onSelectApp: (app: CompetitorApp) => void;
  onCancel: () => void;
}

function SelectAppStep({ onSelectApp, onCancel }: SelectAppStepProps) {
  return (
    <Card>
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Select Your Previous App</h2>
      <p className="text-sm text-gray-500 mb-6">
        Choose the app you&apos;re importing from. We&apos;ll help you map your categories.
        Importing from another app is a Premium feature.
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        {COMPETITOR_APPS.map((app) => (
          <button
            key={app.id}
            type="button"
            onClick={() => onSelectApp(app.id)}
            className="flex flex-col items-center p-6 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <div className="text-primary-600 mb-3">{app.icon}</div>
            <h3 className="text-lg font-medium text-gray-900">{app.name}</h3>
            <p className="mt-1 text-sm text-gray-500 text-center">{app.description}</p>
            <p className="mt-2 text-xs text-gray-400">Supports: {app.fileTypes.join(', ')}</p>
          </button>
        ))}
      </div>

      <div className="mt-6 flex justify-start">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

// ============================================================================
// Step 2: Upload
// ============================================================================

interface UploadStepProps {
  app: CompetitorApp;
  fileInputRef: React.RefObject<HTMLInputElement>;
  isUploading: boolean;
  error: UploadErrorState | null;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUpgrade: () => void;
  onBack: () => void;
  onCancel: () => void;
}

function UploadStep({
  app,
  fileInputRef,
  isUploading,
  error,
  onFileSelect,
  onUpgrade,
  onBack,
  onCancel,
}: UploadStepProps) {
  const appInfo = COMPETITOR_APPS.find((a) => a.id === app);

  return (
    <Card>
      <h2 className="text-lg font-semibold text-gray-900 mb-2">
        Upload {appInfo?.name ?? 'Your'} Export
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        Upload your exported file. We&apos;ll read it and help you map categories; nothing is
        imported until you review the transactions and confirm the account.
      </p>

      <div
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center',
          isUploading ? 'border-primary-300 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={getAcceptedFileTypes(app).join(',')}
          onChange={onFileSelect}
          disabled={isUploading}
          className="hidden"
          id="competitor-file-upload"
          aria-label="Upload file"
        />

        {isUploading ? (
          <div className="flex flex-col items-center">
            <LoadingSpinner className="w-8 h-8 text-primary-600" />
            <p className="mt-4 text-sm text-gray-600">Uploading file...</p>
          </div>
        ) : (
          <label htmlFor="competitor-file-upload" className="cursor-pointer">
            <UploadIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-4 text-sm text-gray-600">
              <span className="font-medium text-primary-600 hover:text-primary-500">
                Click to choose a file
              </span>
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Accepted formats: {appInfo?.fileTypes.join(', ')}
            </p>
          </label>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <div className="flex items-start gap-3">
            <ErrorIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Upload Failed</p>
              <p className="mt-1 text-sm text-red-700">{error.message}</p>
              {error.showUpgrade && (
                <div className="mt-3">
                  <Button variant="primary" onClick={onUpgrade}>
                    Upgrade to Premium
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-medium text-blue-800">How to export from {appInfo?.name}</h4>
        <ExportInstructions app={app} />
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

function ExportInstructions({ app }: { app: CompetitorApp }) {
  switch (app) {
    case 'mint':
      return (
        <ol className="mt-2 text-sm text-blue-700 list-decimal list-inside space-y-1">
          <li>Use the transactions.csv file you exported from Mint</li>
          <li>Upload it here</li>
        </ol>
      );
    case 'ynab':
      return (
        <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
          <li>CSV: the register CSV from a YNAB export</li>
          <li>
            JSON: the transactions of one account as returned by the YNAB API, saved to a
            .json file
          </li>
          <li>JSON files must contain one account; upload one file per account</li>
        </ul>
      );
    case 'quicken':
      return (
        <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
          <li>In Quicken, export one account as a QIF file (or download a QFX file)</li>
          <li>QIF files must contain one account; upload one file per account</li>
          <li>Split transactions are imported as one line per split</li>
        </ul>
      );
    default:
      return null;
  }
}

// ============================================================================
// Step 3: Processing
// ============================================================================

interface ProcessingStepProps {
  status: string | null;
  failed: boolean;
  failureMessage: string | null;
  isOrphaned: boolean;
  onStartOver: () => void;
  onCancel: () => void;
}

function ProcessingStep({
  status,
  failed,
  failureMessage,
  isOrphaned,
  onStartOver,
  onCancel,
}: ProcessingStepProps) {
  if (failed || isOrphaned) {
    return (
      <Card>
        <div className="text-center py-10" role="alert">
          <ErrorIcon className="mx-auto h-12 w-12 text-red-400" />
          <h2 className="mt-4 text-lg font-medium text-gray-900">
            {failed ? 'We could not import this file' : 'No transactions to import'}
          </h2>
          <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
            {failed
              ? failureMessage || 'There was a problem processing your file. Please try again.'
              : 'The file was processed but contained no transactions to review.'}
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onStartOver}>
              Upload Another File
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-col items-center justify-center py-12" aria-live="polite">
        <LoadingSpinner />
        <span className="mt-3 text-gray-900 font-medium">Processing your file...</span>
        {status && <span className="mt-1 text-sm text-gray-500">Status: {status}</span>}
      </div>
      <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <InfoIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Next you&apos;ll map categories, then confirm the account and review the transactions
            before anything is imported.
          </p>
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin h-6 w-6 text-primary-600', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  );
}

// Competitor App Icons (simplified)
function MintIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z" />
    </svg>
  );
}

function YnabIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
    </svg>
  );
}

function QuickenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm1-13h-2v6l5.25 3.15.75-1.23-4-2.42V7z" />
    </svg>
  );
}
